import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { hasCapability, sessionApi, sessionChanged, sessionQuery } from '../api/session';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import { Alert, Button, Loading, SIGNED_OUT, Centered as UiCentered } from '@avrahami/ui';

/** Full-page frame for the screens outside the signed-in app (sign-in, invitation). */
export function Centered({ children }: { children: ReactNode }) {
  return <UiCentered product={he.app.product}>{children}</UiCentered>;
}

function NavLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
      activeProps={{ className: 'bg-brand-50 font-semibold text-brand-900' }}
    >
      {children}
    </Link>
  );
}

/**
 * The signed-in shell. It decides, from the session, where the user belongs: sign-in, the second
 * step, enrollment, "no platform access", or the app itself. The API enforces all of this again;
 * the shell only avoids showing screens that would fail.
 */
export function AppLayout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const session = useQuery(sessionQuery);
  const logout = useMutation({
    mutationFn: sessionApi.logout,
    onSettled: () => {
      sessionChanged(queryClient, SIGNED_OUT);
    },
  });

  const state = session.data;
  const destination =
    state === undefined
      ? null
      : state.kind !== 'signed-in'
        ? 'login'
        : state.me.platform === null
          ? 'no-access'
          : state.me.mfa.enabled
            ? null
            : 'mfa-setup';
  // Runs when the answer changes, not on every render: the address is read once, so the
  // redirect back after signing in cannot feed on itself.
  useEffect(() => {
    if (destination === 'login') {
      const back = `${window.location.pathname}${window.location.search}`;
      void navigate({ to: '/login', search: { redirect: back }, replace: true });
    } else if (destination === 'no-access') {
      void navigate({ to: '/no-access', replace: true });
    } else if (destination === 'mfa-setup') {
      void navigate({ to: '/mfa/setup', replace: true });
    }
  }, [destination, navigate]);

  if (session.isError) {
    return (
      <Centered>
        <Alert>{errorMessage(session.error)}</Alert>
      </Centered>
    );
  }
  if (state?.kind !== 'signed-in' || destination !== null) {
    return (
      <Centered>
        <Loading />
      </Centered>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3">
          <span className="me-4 font-bold text-brand-900">{he.app.title}</span>
          <nav className="flex flex-1 flex-wrap gap-1">
            <NavLink to="/tenants">{he.nav.tenants}</NavLink>
            {hasCapability(state, 'platform.tenants.read') ? (
              <NavLink to="/domains">{he.nav.domains}</NavLink>
            ) : null}
            {hasCapability(state, 'platform.tenants.read') ? (
              <NavLink to="/service">{he.nav.service}</NavLink>
            ) : null}
            {hasCapability(state, 'platform.catalog.read') ? (
              <NavLink to="/catalog">{he.nav.catalog}</NavLink>
            ) : null}
            {hasCapability(state, 'platform.ai.view') ? (
              <NavLink to="/ai">{he.nav.ai}</NavLink>
            ) : null}
            {hasCapability(state, 'platform.audit.read') ? (
              <NavLink to="/audit">{he.nav.audit}</NavLink>
            ) : null}
            {hasCapability(state, 'platform.users.manage') ? (
              <NavLink to="/staff">{he.nav.staff}</NavLink>
            ) : null}
            <NavLink to="/account">{he.nav.account}</NavLink>
          </nav>
          <span className="ltr text-xs text-slate-500">{state.me.user.email}</span>
          <Button
            tone="secondary"
            busy={logout.isPending}
            onClick={() => {
              logout.mutate();
            }}
          >
            {he.nav.logout}
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}
